import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  IconButton,
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  Stack,
  useTheme,
  alpha,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  QrCodeScanner,
  Person,
  Work,
  AccountBalance,
  Lock,
  Security,
  ContactPhone,
} from '@mui/icons-material';
import PercentIcon from '@mui/icons-material/Percent';
import HomeIcon from '@mui/icons-material/Home';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ImageUpload from '../ImageUpload/ImageUpload';
import * as actions from '../../redux/actions';
import { sortProjectRows } from '../../utils/functions/sortProjects';

export function EmployeeForm({
  errors,
  roletype,
  role,
  employee,
  Controller,
  control,
  addUser,
  setAddUser,
  employeeImage,
  setEmployeeImage,
  department,
  handleOpen,
  qr,
  watch,
  setValue,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const roleState = useSelector((state) => state.role);

  useEffect(() => {
    if (!roleState?.rows?.length) dispatch(actions.roleAll());
  }, []);
  const projectAccessMode = watch('project_access_mode');
  const [openProjectModal, setOpenProjectModal] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState(employee?.assigned_projects ?? []);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');

  useEffect(() => {
    if (openProjectModal) dispatch(actions.projectAll({ lite: true }));
  }, [openProjectModal]);

  const sec = (icon, title) => (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
      {React.cloneElement(icon, { sx: { color: 'text.secondary', fontSize: '1rem' } })}
      <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ whiteSpace: 'nowrap', fontSize: '0.78rem', letterSpacing: 0.3 }}>
        {title}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: 'divider' }} />
    </Stack>
  );

  const p = { p: 2, mb: 1.5, borderRadius: 1.5, overflow: 'hidden', bgcolor: 'grey.50' };

  return (
    <Box sx={{
      width: '100%',
      overflowX: 'hidden',
      '& .MuiInputBase-input:not([readonly])': { color: 'primary.main' },
      '& .MuiSelect-select': { color: 'primary.main' },
    }}>

      {/* User account (add mode) */}
      {addUser != null && (
        <Paper elevation={0} sx={{ ...p, mb: 1.5 }}>
          <FormControlLabel
            defaultValue={addUser}
            control={<Checkbox color="primary" size="small" />}
            label={<Typography variant="body2" fontWeight={500}>Create employee with user account</Typography>}
            onChange={() => setAddUser(!addUser)}
          />
          {addUser === true && (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={4}>
                <Controller name="username" control={control} defaultValue={employee?.username || ''} rules={{ required: true }}
                  render={({ field }) => <TextField {...field} label="Username" fullWidth size="small" required error={!!errors.username} />} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="password" control={control} defaultValue="" rules={{ required: true }}
                  render={({ field }) => <TextField {...field} label="Password" type="password" fullWidth size="small" required error={!!errors.password} />} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="confirmPassword" control={control} defaultValue="" rules={{ required: true }}
                  render={({ field }) => <TextField {...field} label="Confirm Password" type="password" fullWidth size="small" required />} />
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* ── 3-column layout (lg), 2-column (md), 1-column (xs) ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
        gap: 1.5,
        width: '100%',
      }}>

        {/* ── COL 1: Personal Info + Emergency Contact ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>

          <Paper elevation={0} sx={p}>
            {sec(<Person fontSize="small" color="primary" />, 'Personal Information')}
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Controller name="firstname" control={control} defaultValue={employee?.firstname || ''} rules={{ required: true }}
                  render={({ field }) => <TextField {...field} label="First Name (EN)" fullWidth size="small" required error={!!errors.firstname} />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="lastname" control={control} defaultValue={employee?.lastname || ''} rules={{ required: true }}
                  render={({ field }) => <TextField {...field} label="Last Name (EN)" fullWidth size="small" required error={!!errors.lastname} />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="firstname_th" control={control} defaultValue={employee?.firstname_th || ''}
                  render={({ field }) => <TextField {...field} label="ชื่อ (ภาษาไทย)" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="lastname_th" control={control} defaultValue={employee?.lastname_th || ''}
                  render={({ field }) => <TextField {...field} label="นามสกุล (ภาษาไทย)" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="phone_number" control={control} defaultValue={employee?.phone_number || ''}
                  render={({ field }) => <TextField {...field} label="Phone Number" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="tax_id" control={control} defaultValue={employee?.tax_id || ''}
                  render={({ field }) => <TextField {...field} label="National ID" fullWidth size="small" />} />
              </Grid>

              {/* ── คำนำหน้า / เพศ / วันเกิด / อายุ ── */}
              <Grid item xs={3}>
                <Controller name="title" control={control} defaultValue={employee?.title || ''}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>คำนำหน้า</InputLabel>
                      <Select {...field} label="คำนำหน้า">
                        <MenuItem value="นาย">นาย</MenuItem>
                        <MenuItem value="นาง">นาง</MenuItem>
                        <MenuItem value="นางสาว">นางสาว</MenuItem>
                        <MenuItem value="เด็กชาย">เด็กชาย</MenuItem>
                        <MenuItem value="เด็กหญิง">เด็กหญิง</MenuItem>
                        <MenuItem value="Mr.">Mr.</MenuItem>
                        <MenuItem value="Mrs.">Mrs.</MenuItem>
                        <MenuItem value="Miss">Miss</MenuItem>
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={3}>
                <Controller name="gender" control={control} defaultValue={employee?.gender || ''}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>เพศ</InputLabel>
                      <Select {...field} label="เพศ">
                        <MenuItem value="ชาย">ชาย</MenuItem>
                        <MenuItem value="หญิง">หญิง</MenuItem>
                        <MenuItem value="อื่นๆ">อื่นๆ</MenuItem>
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={4}>
                <Controller name="date_of_birth" control={control}
                  defaultValue={employee?.date_of_birth ? new Date(employee.date_of_birth).toISOString().split('T')[0] : ''}
                  render={({ field }) => (
                    <TextField {...field} label="วันเกิด" fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} />
                  )} />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="อายุ"
                  fullWidth
                  size="small"
                  value={(() => {
                    const dob = watch('date_of_birth');
                    if (!dob) return '';
                    const d = new Date(dob);
                    if (isNaN(d.getTime())) return '';
                    const today = new Date();
                    let age = today.getFullYear() - d.getFullYear();
                    const m = today.getMonth() - d.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
                    return age >= 0 ? `${age} ปี` : '';
                  })()}
                  InputProps={{ readOnly: true }}
                  inputProps={{ style: { color: '#888', textAlign: 'center' } }}
                />
              </Grid>

              {/* ── ศาสนา / สถานภาพ / บุตร ── */}
              <Grid item xs={5}>
                <Controller name="religion" control={control} defaultValue={employee?.religion || ''}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>ศาสนา</InputLabel>
                      <Select {...field} label="ศาสนา">
                        <MenuItem value="พุทธ">พุทธ</MenuItem>
                        <MenuItem value="อิสลาม">อิสลาม</MenuItem>
                        <MenuItem value="คริสต์">คริสต์</MenuItem>
                        <MenuItem value="พราหมณ์-ฮินดู">พราหมณ์-ฮินดู</MenuItem>
                        <MenuItem value="ไม่ระบุ">ไม่ระบุ</MenuItem>
                        <MenuItem value="อื่นๆ">อื่นๆ</MenuItem>
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={5}>
                <Controller name="marital_status" control={control} defaultValue={employee?.marital_status || ''}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>สถานภาพ</InputLabel>
                      <Select {...field} label="สถานภาพ">
                        <MenuItem value="โสด">โสด</MenuItem>
                        <MenuItem value="สมรส">สมรส</MenuItem>
                        <MenuItem value="หย่าร้าง">หย่าร้าง</MenuItem>
                        <MenuItem value="หม้าย">หม้าย</MenuItem>
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={2}>
                <Controller name="children_count" control={control} defaultValue={employee?.children_count ?? 0}
                  render={({ field }) => (
                    <TextField {...field} label="บุตร (คน)" fullWidth size="small" type="number"
                      InputProps={{ inputProps: { min: 0 } }} />
                  )} />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  {(employee?.image || employee?.id_card_image) && (
                    <Box sx={{ maxWidth: 90, flexShrink: 0 }}>
                      <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.5 }}>
                        {employee?.image ? 'รูปปัจจุบัน' : 'รูปบัตรประชาชน'}
                      </Typography>
                      <img
                        src={employee?.image?.url || employee?.id_card_image?.url}
                        alt="Employee"
                        onClick={() => { setPreviewSrc(employee?.image?.url || employee?.id_card_image?.url); setPreviewOpen(true); }}
                        style={{ height: 80, width: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0', cursor: 'zoom-in' }}
                      />
                    </Box>
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <ImageUpload preview_size="120" maxNumber={1} images={employeeImage} setImages={setEmployeeImage}
                      title={employee?.rows ? 'Upload Photo' : 'Edit Photo'} />
                  </Box>
                </Stack>
              </Grid>

              {/* Photo preview lightbox */}
              <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" PaperProps={{ sx: { bgcolor: '#111' } }}>
                <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img src={previewSrc} alt="preview" style={{ maxWidth: '80vw', maxHeight: '80vh', borderRadius: 4 }} />
                </DialogContent>
                <DialogActions sx={{ bgcolor: '#111', py: 0.5 }}>
                  <Button size="small" onClick={() => setPreviewOpen(false)} sx={{ color: 'grey.400' }}>ปิด</Button>
                </DialogActions>
              </Dialog>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...p, flexGrow: 1 }}>
            {sec(<ContactPhone fontSize="small" color="primary" />, 'Emergency Contact')}
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Controller name="emergency_contact.name" control={control} defaultValue={employee?.emergency_contact?.name || ''}
                  render={({ field }) => <TextField {...field} label="ชื่อผู้ติดต่อฉุกเฉิน" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="emergency_contact.phone" control={control} defaultValue={employee?.emergency_contact?.phone || ''}
                  render={({ field }) => <TextField {...field} label="เบอร์โทร" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="emergency_contact.relationship" control={control} defaultValue={employee?.emergency_contact?.relationship || ''}
                  render={({ field }) => <TextField {...field} label="ความสัมพันธ์" fullWidth size="small" placeholder="เช่น ภรรยา, บิดา" />} />
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* ── COL 2: Address + Bank ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>

          <Paper elevation={0} sx={p}>
            {sec(<HomeIcon fontSize="small" color="primary" />, 'Address Information')}
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Controller name="address.house_number" control={control} defaultValue={employee?.address?.house_number || ''}
                  render={({ field }) => <TextField {...field} label="House No." fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="address.village_number" control={control} defaultValue={employee?.address?.village_number || ''}
                  render={({ field }) => <TextField {...field} label="Village" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={12}>
                <Controller name="address.road" control={control} defaultValue={employee?.address?.road || ''}
                  render={({ field }) => <TextField {...field} label="Road/Street" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="address.subdistrict" control={control} defaultValue={employee?.address?.subdistrict || ''}
                  render={({ field }) => <TextField {...field} label="Subdistrict" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="address.district" control={control} defaultValue={employee?.address?.district || ''}
                  render={({ field }) => <TextField {...field} label="District" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="address.province" control={control} defaultValue={employee?.address?.province || ''}
                  render={({ field }) => <TextField {...field} label="Province" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={3}>
                <Controller name="address.postcode" control={control} defaultValue={employee?.address?.postcode || ''}
                  render={({ field }) => <TextField {...field} label="Postcode" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={3}>
                <Controller name="address.country" control={control} defaultValue={employee?.address?.country || 'Thailand'}
                  render={({ field }) => <TextField {...field} label="Country" fullWidth size="small" />} />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...p, flexGrow: 1 }}>
            {sec(<AccountBalance fontSize="small" color="primary" />, 'Bank Information')}
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Controller name="bank_account.number" control={control} defaultValue={employee?.bank_account?.number || ''}
                  render={({ field }) => <TextField {...field} label="Account Number" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="bank_account.account_name" control={control} defaultValue={employee?.bank_account?.account_name || ''}
                  render={({ field }) => <TextField {...field} label="Account Name" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="bank_account.bank_name" control={control} defaultValue={employee?.bank_account?.bank_name || ''}
                  render={({ field }) => <TextField {...field} label="Bank Name" fullWidth size="small" />} />
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* ── COL 3: Employment + Salary + System Access ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>

          <Paper elevation={0} sx={p}>
            {sec(<Work fontSize="small" color="primary" />, 'Employment Information')}
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Controller name="type" control={control} defaultValue={employee?.type || ''} rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl required fullWidth size="small" error={!!errors.type}>
                      <InputLabel>Employee Type</InputLabel>
                      <Select {...field} label="Employee Type">
                        <MenuItem value="FULLTIME">Full-time</MenuItem>
                        <MenuItem value="DAILY">Daily Worker</MenuItem>
                        <MenuItem value="CONTRACT">Contract Worker</MenuItem>
                        <MenuItem value="OUTSOURCE">Outsource</MenuItem>
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="department" control={control} defaultValue={employee?.department?._id || ''} rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl required fullWidth size="small" error={!!errors.department}>
                      <InputLabel>Department</InputLabel>
                      <Select {...field} label="Department">
                        {_.size(department?.rows)
                          ? _.map(department.rows, (r) => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)
                          : <MenuItem disabled value=""><em>No data</em></MenuItem>}
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="role" control={control} defaultValue={employee?.role?.id || ''} rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl required fullWidth size="small" error={!!errors.role}>
                      <InputLabel>Position</InputLabel>
                      <Select {...field} label="Position">
                        {_.size(roletype?.rows)
                          ? _.map(roletype.rows, (r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)
                          : <MenuItem disabled value=""><em>No data</em></MenuItem>}
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="check_key" control={control} defaultValue={employee?.check_key || qr || ''}
                  render={({ field }) => (
                    <TextField {...field} label="QR Code" fullWidth size="small"
                      InputProps={{ endAdornment: handleOpen && (
                        <IconButton edge="end" onClick={handleOpen} size="small"><QrCodeScanner fontSize="small" /></IconButton>
                      )}} />
                  )} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="team" control={control} defaultValue={employee?.team || ''}
                  render={({ field }) => <TextField {...field} label="Team" fullWidth size="small" />} />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="date_start"
                  control={control}
                  defaultValue={
                    employee?.date_start
                      ? new Date(employee.date_start).toISOString().split('T')[0]
                      : new Date().toISOString().split('T')[0]
                  }
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="วันที่เริ่มงาน"
                      fullWidth
                      size="small"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller name="timestamp_use_location" control={control} defaultValue={employee?.timestamp_use_location ?? false}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>รูปแบบลงเวลา</InputLabel>
                      <Select {...field} label="รูปแบบลงเวลา"
                        value={field.value === true ? 'location' : 'manual'}
                        onChange={(e) => field.onChange(e.target.value === 'location')}>
                        <MenuItem value="manual">เลือกโครงการเอง</MenuItem>
                        <MenuItem value="location">GPS (1 กม.)</MenuItem>
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="require_checkin_photo" control={control} defaultValue={employee?.require_checkin_photo ?? true}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>ถ่ายรูปลงเวลา</InputLabel>
                      <Select {...field} label="ถ่ายรูปลงเวลา"
                        value={field.value === false ? 'no' : 'yes'}
                        onChange={(e) => field.onChange(e.target.value !== 'no')}>
                        <MenuItem value="yes">ต้องถ่ายรูป</MenuItem>
                        <MenuItem value="no">ไม่ต้องถ่ายรูป</MenuItem>
                      </Select>
                    </FormControl>
                  )} />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Controller name="project_access_mode" control={control} defaultValue={employee?.project_access_mode ?? false}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>สิทธิ์โครงการ</InputLabel>
                        <Select {...field} label="สิทธิ์โครงการ">
                          <MenuItem value={false}>ทุกโครงการ</MenuItem>
                          <MenuItem value={true}>เฉพาะที่กำหนด</MenuItem>
                        </Select>
                      </FormControl>
                    )} />
                  {projectAccessMode === true && (
                    <Button variant="outlined" size="small" onClick={() => setOpenProjectModal(true)} sx={{ whiteSpace: 'nowrap' }}>
                      + กำหนด
                    </Button>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider><Typography variant="caption" color="text.secondary">Salary</Typography></Divider>
              </Grid>

              <Grid item xs={4}>
                <Controller name="salary.month" control={control} defaultValue={employee?.salary?.month || 0}
                  render={({ field }) => <TextField {...field} label="Salary" fullWidth size="small" type="number" InputProps={{ inputProps: { min: 0 } }} />} />
              </Grid>
              <Grid item xs={4}>
                <Controller name="salary_extra.month" control={control} defaultValue={employee?.salary_extra?.month || 0}
                  render={({ field }) => <TextField {...field} label="Extra/Month" fullWidth size="small" type="number" InputProps={{ inputProps: { min: 0 } }} />} />
              </Grid>
              <Grid item xs={4}>
                <Controller name="salary_extra.day" control={control} defaultValue={employee?.salary_extra?.day || 0}
                  render={({ field }) => <TextField {...field} label="Extra/Day" fullWidth size="small" type="number" InputProps={{ inputProps: { min: 0 } }} />} />
              </Grid>

              <Grid item xs={12}>
                <Divider><Typography variant="caption" color="text.secondary">ค่าออกสนาม</Typography></Divider>
              </Grid>

              {/* row 1: ค่าออกสนาม checkbox | override amount */}
              <Grid item xs={6}>
                <Controller name="field_allowance.enabled" control={control} defaultValue={employee?.field_allowance?.enabled || false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} size="small" />}
                      label={<Typography variant="body2">มีสิทธิ์ได้ค่าออกสนาม</Typography>} />
                  )} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="field_allowance.override_amount" control={control} defaultValue={employee?.field_allowance?.override_amount ?? ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value === null ? '' : field.value}
                      onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                      label="บาท/วัน"
                      fullWidth
                      size="small"
                      type="number"
                      InputProps={{ inputProps: { min: 0 } }}
                      helperText="เว้นว่างไว้ = ใช้อัตราส่วนกลาง"
                      FormHelperTextProps={{ sx: { fontSize: '0.65rem', color: 'text.disabled', mt: 0.25 } }}
                      placeholder="เช่น 300"
                    />
                  )} />
              </Grid>
              <Grid item xs={12}>
                <Divider><Typography variant="caption" color="text.secondary">ภาษีรัฐบาล</Typography></Divider>
              </Grid>
              {/* row 2: ขึ้นประกันสังคม checkbox | หักภาษี checkbox */}
              <Grid item xs={6}>
                <Controller name="sso_tax.status" control={control} defaultValue={employee?.sso_tax?.status ?? true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} size="small" />}
                      label={<Typography variant="body2">ขึ้นประกันสังคม</Typography>} />
                  )} />
              </Grid>
              <Grid item xs={6}>
                <Controller name="tax.status" control={control} defaultValue={employee?.tax?.status || false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} size="small" />}
                      label={<Typography variant="body2">หักภาษี ณ ที่จ่าย</Typography>} />
                  )} />
              </Grid>
              {/* row 3: SSO rate | empty */}
              <Grid item xs={6}>
                <Controller name="tax.number" control={control} defaultValue={typeof employee?.tax?.number === 'number' ? employee.tax.number : 3}
                  render={({ field }) => (
                    <TextField {...field} label="อัตรา SSO (%)" fullWidth size="small" type="number"
                      InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 }, endAdornment: <InputAdornment position="end"><PercentIcon fontSize="small" /></InputAdornment> }} />
                  )} />
              </Grid>
              <Grid item xs={6} />
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...p, flexGrow: 1 }}>
            {sec(<Security fontSize="small" color="primary" />, 'System Access')}
            <Controller
              name="system_role"
              control={control}
              defaultValue={
                (employee?.system_role || []).map((r) => r?.id || r?._id || String(r)).filter(Boolean)
              }
              render={({ field }) => {
                const ROLE_COLORS = ['#1976d2','#2e7d32','#7b1fa2','#e65100','#c62828','#00838f'];
                const allRoles = roleState?.rows || role?.rows || [];
                const selectedIds = field.value || [];
                const selectedRoles = allRoles.filter((r) => selectedIds.includes(r.id || r._id));
                const unselectedRoles = allRoles.filter((r) => !selectedIds.includes(r.id || r._id));
                return (
                  <Stack spacing={1.5}>
                    {/* assigned roles */}
                    {selectedRoles.map((r, idx) => {
                      const color = ROLE_COLORS[allRoles.indexOf(r) % ROLE_COLORS.length];
                      return (
                        <Box
                          key={r.id || r._id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            py: 1,
                            borderRadius: 1.5,
                            border: `1.5px solid ${color}`,
                            bgcolor: alpha(color, 0.06),
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color }}>
                              {r.name}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => field.onChange(selectedIds.filter((sid) => sid !== (r.id || r._id)))}
                            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' }, p: 0.5 }}
                          >
                            <Typography variant="caption" sx={{ lineHeight: 1, fontSize: 14 }}>✕</Typography>
                          </IconButton>
                        </Box>
                      );
                    })}

                    {/* add role dropdown */}
                    {unselectedRoles.length > 0 && (
                      <Autocomplete
                        size="small"
                        options={unselectedRoles}
                        getOptionLabel={(opt) => opt.name}
                        value={null}
                        filterOptions={(x) => x}
                        onChange={(_, picked) => {
                          if (picked) field.onChange([...selectedIds, picked.id || picked._id]);
                        }}
                        renderOption={(props, opt) => {
                          const color = ROLE_COLORS[allRoles.indexOf(opt) % ROLE_COLORS.length];
                          return (
                            <li {...props} key={opt.id || opt._id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                                <Typography variant="body2">{opt.name}</Typography>
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="+ เพิ่มบทบาท" size="small" />
                        )}
                      />
                    )}

                    {allRoles.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ยังไม่มีบทบาทในระบบ
                      </Typography>
                    )}
                    {allRoles.length > 0 && selectedRoles.length === 0 && unselectedRoles.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        กำหนดบทบาทครบทุกรายการแล้ว
                      </Typography>
                    )}
                  </Stack>
                );
              }}
            />
          </Paper>
        </Box>
      </Box>

      {/* Project selection modal */}
      <Dialog open={openProjectModal} onClose={() => setOpenProjectModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>กำหนดโครงการที่อนุญาต</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete multiple options={sortProjectRows(project?.rows)} value={selectedProjects}
              onChange={(_, v) => setSelectedProjects(v)}
              getOptionLabel={(o) => `${o.project_number ?? ''} - ${o.name ?? ''} (${o.customer ?? '-'})`}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              renderTags={() => null}
              renderInput={(params) => <TextField {...params} label="เลือกโครงการ" size="small" placeholder="พิมพ์เพื่อค้นหา..." />} />
            {selectedProjects.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">โครงการที่กำหนด ({selectedProjects.length})</Typography>
                {selectedProjects.map((proj) => (
                  <Paper key={proj._id} elevation={0} sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{proj.project_number} — {proj.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{proj.customer ?? '-'}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setSelectedProjects((prev) => prev.filter((x) => x._id !== proj._id))}>
                      <Typography variant="caption">✕</Typography>
                    </IconButton>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProjectModal(false)}>ปิด</Button>
          <Button variant="contained" onClick={() => { setValue('assigned_projects', selectedProjects.map((proj) => proj._id)); setOpenProjectModal(false); }}>บันทึก</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

EmployeeForm.propTypes = {
  roletype: PropTypes.object,
  role: PropTypes.object,
  errors: PropTypes.shape({
    username: PropTypes.object,
    password: PropTypes.object,
    firstname: PropTypes.object,
    lastname: PropTypes.object,
    phone_number: PropTypes.object,
    department: PropTypes.object,
    role: PropTypes.object,
  }),
  employeeImage: PropTypes.arrayOf(PropTypes.object),
  setEmployeeImage: PropTypes.func.isRequired,
  addUser: PropTypes.bool,
  setAddUser: PropTypes.func,
  employee: PropTypes.object,
  Controller: PropTypes.func,
  control: PropTypes.object,
  handleOpen: PropTypes.func,
  qr: PropTypes.string,
};

EmployeeForm.defaultProps = {
  employee: null,
  addUser: null,
};

export default EmployeeForm;
